import { EErrorCode } from 'config/errors-codes';
import { errorCollection } from 'config/ui/error-collection';
import { IPublicSession, startNewSession, userSession } from 'state/session';
import { showToast } from 'state/toast';
import { user } from 'utils/auth/user';
import { getImages } from 'utils/network/getCarImages';
import { tenantConfig } from 'utils/tenant';
import { i18n } from '@lingui/core';
import { State } from 'utils/state';
import logger from 'utils/log';
import { isObject } from 'utils/objects';
import { ICarImageElement, IVehicle } from 'data/schema';
import { updateVehicle } from './vehicleRegistry';
import { generateThumbnailUrl } from 'utils/network/preload';
import { clampImageIndex, selectImage } from 'utils/sync/imageSelection';

/**
 * Number of image requests currently in flight.
 *
 * AVS-4353: the bootstrap's release guard cannot tell its own claim apart from one `fetchImages`
 * made — both are `carImagesStatus: 'loading'` on the same prString. Releasing while a real
 * request is outstanding drops the user back to an empty gallery even though the images are still
 * on the wire, which `avs4355-fullscreen-exit.spec.ts` reproduces by hanging `/product-images`.
 *
 * AVS-4381: each claim now expires on its own. This was a plain counter incremented before the
 * await and decremented in `.finally`, so a request that never settled held the claim forever —
 * and `sendApiRequest` carries an AbortController but no timeout, which makes "never" reachable.
 * One stalled `/product-images` therefore disabled the release guard for the lifetime of the page
 * and every later bootstrap stranded at 'loading'. (The same leak silently disabled the guard for
 * every unit test that ran after the AVS-4355 case in `carImages.sessionStart.test.ts`.)
 *
 * Only OUR CLAIM on the gallery is bounded — the request itself is left alone, so a slow response
 * that eventually lands still renders through the normal path.
 */
const IMAGE_FETCH_CLAIM_TTL_MS = 30_000;

/**
 * How long the session bootstrap may hold the gallery while `/sessions` registers (AVS-4383
 * review). `startNewSession()` goes through the same `sendApiRequest` as the image fetch — an
 * AbortController that only fires on a duplicate request, never on a clock — so the `await` in
 * `runSessionForImages` is not guaranteed to resolve. Without this the PR's "every claim expires"
 * guarantee had a hole: the same permanent spinner, reached through `/sessions` instead of
 * `/product-images`, and the image TTL does not touch it.
 *
 * As with `IMAGE_FETCH_CLAIM_TTL_MS`, only the CLAIM is bounded. The POST is left running, and a
 * registration that eventually lands still reaches the auto-updater through its `id` change.
 */
const SESSION_REGISTRATION_CLAIM_TTL_MS = 30_000;

let liveImageFetchClaims = 0;

/**
 * Monotonic id of the newest image request (AVS-4383 review). A bare `claimedTheSpinner` flag
 * proves a request once flipped 'unset' → 'loading'; it does NOT prove the current 'loading' is
 * still that write. When fetch A claims the spinner, the car switches, and fetch B starts — B
 * cannot claim, the status is already 'loading' — the visible spinner now backs B's request
 * even though A wrote it. A settling stale (or rejecting) then handed back B's spinner.
 */
let latestImageFetchGeneration = 0;

/** The request generation that currently owes the optimistic 'loading' back, if any. */
let spinnerClaimOwner: number | null = null;

/**
 * Register a new image request and settle who owns the visible spinner. Ownership always sits
 * with the NEWEST request: either it wrote the 'loading' itself, or it superseded the in-flight
 * request that did — the spinner on screen backs the newest request either way, so the duty to
 * hand it back moves with it. (Expiring the older claim without transferring it strands the
 * spinner when the newest request declines — the generated invariant suite catches that.)
 */
function beginImageFetchGeneration(claimedTheSpinner: boolean): number {
  const generation = ++latestImageFetchGeneration;
  if (claimedTheSpinner || spinnerClaimOwner !== null) {
    spinnerClaimOwner = generation;
  }
  return generation;
}

/**
 * Take an ownership claim on the gallery for one in-flight image request.
 * @returns a release function that is safe to call more than once.
 */
function claimImageFetch(): () => void {
  liveImageFetchClaims += 1;

  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    liveImageFetchClaims -= 1;
  };

  // The claim outliving its request is the whole defect, so bound it. Double-release is possible
  // (expiry fires, then the request finally settles) — `released` makes that a no-op rather than
  // driving the counter negative, which would leave the guard permanently open instead of
  // permanently shut.
  const expiry = setTimeout(release, IMAGE_FETCH_CLAIM_TTL_MS);
  // A pending timer keeps the Node event loop alive; ours must never hold a test run open.
  (expiry as unknown as { unref?: () => void }).unref?.();

  return () => {
    clearTimeout(expiry);
    release();
  };
}

/**
 * Backwards compatibility bridge for deprecated currentImage field
 * Warns users and redirects to new selectImage() API
 */
export function initCurrentImageBridge(state: State<IPublicSession>) {
  let hasWarned = false;

  state.on('change', (changedKeys, changedValues) => {
    // `changedValues` is a positional array parallel to `changedKeys` (see State.setValues),
    // NOT a keyed object — read the new value by index. The previous `changedValues.currentImage`
    // was always `undefined`, so this bridge silently never fired.
    const index = changedKeys.indexOf('currentImage');
    const newValue = index === -1 ? undefined : changedValues[index];

    // Only bridge a real numeric image index. `changedValues` is loosely typed and this is a
    // deprecated integrator-facing field, so guard against a non-number (null/garbage) rather
    // than casting it straight through to selectImage().
    if (typeof newValue === 'number') {
      // Only warn once per session to avoid console spam
      if (!hasWarned) {
        logger.warn(
          '[DEPRECATED] session.currentImage is deprecated and will be removed in a future version. ' +
            'Use runtime.selectImage(index) instead. ' +
            'See documentation: https://docs.example.com/image-selection'
        );
        hasWarned = true;
      }

      // Bridge to new system for backwards compatibility
      selectImage(newValue);
    }
  });
}

/**
 * Whether claiming the gallery with a 'loading' status can actually be redeemed by a fetch.
 *
 * AVS-4353: used ONLY by `startSessionForImages`, deliberately NOT by
 * `initCarImagesAutoUpdate`. The auto-updater's own guard must stay exactly as it was:
 * when 2D is disallowed it still calls `fetchImages()`, whose `is2DFeatureAllowed()` check
 * carries the side effect of forcing `activeExperience` to '3D'. Other code (notably
 * `canOpenSharingDashboard`) depends on that flip, so suppressing the call here would
 * silently change 3D-fallback behaviour — it did, and AVS-3995's E2E caught it.
 */
function canClaimGalleryForFetch(session: IPublicSession): boolean {
  return (
    // fetchImages() bails on is2DFeatureAllowed() before issuing a request, so with 2D
    // disallowed no fetch can ever follow and the claim would never be released.
    tenantConfig().isAllowed('2D') &&
    session.imageOverrideMode !== 'manual' &&
    !!session.prString &&
    // images for this exact car are already loaded — nothing left to fetch
    session.carImages?.prString !== session.prString
  );
}

export function initCarImagesAutoUpdate(state: State<IPublicSession>) {
  state.on('change', (changedKeys, changedValues, oldValues, newValues, session) => {
    // Self-heal the stranded gallery (AVS-4381 / AVS-4383): images loaded for the car we are
    // actually showing, but a status that hides them. Nothing below can recover this — the fetch
    // guard treats "already loaded" images as nothing to do — so the gallery stays blank until
    // the car changes, which is exactly the reported symptom.
    //
    // Repairing the status beats refetching: it needs no network, cannot double-fetch against an
    // in-flight request, and restores precisely the invariant that was broken. It also heals
    // sessions ALREADY persisted in this state by an older client — `carImagesStatus` is on the
    // synced schema, so those records exist on the server today and would otherwise stay broken
    // after this fix ships.
    //
    // Runs before the manual-override bail: an integrator override does not make a hidden gallery
    // correct, and `showImages` is what set both fields in that case anyway.
    if (
      session.carImages &&
      session.carImages.prString === session.prString &&
      session.carImages.items.length > 0 &&
      session.carImagesStatus !== 'ready'
    ) {
      userSession().setValues({ carImagesStatus: 'ready' });
      return;
    }

    // Skip auto-update if integrator has taken control of images
    if (session.imageOverrideMode === 'manual') {
      return;
    }

    // One of these keys has to change in order to resume the auto-update
    const significantChanges = ['prString', 'activeExperience', 'id', 'imageOverrideMode', 'comparisonMode'].some(
      (key) => changedKeys.includes(key)
    );

    // Comparison mode requires 2D images even while in 3D experience
    const inComparisonMode = session.comparisonMode === 'selecting' || session.comparisonMode === 'on';

    // The session must meet these requirements to fetch images
    const minimalDataPresent =
      user.type === 'dealer' &&
      session.id &&
      (session.activeExperience === '2D' || inComparisonMode) &&
      session.prString;

    // AVS-4161: When carImages is externally cleared (e.g., 3D stream's onUpdatePrString
    // fires with the same prString), none of the "significant" keys change. Detect this
    // specific case — carImages went from defined to undefined — and re-fetch.
    const imagesClearedExternally =
      changedKeys.includes('carImages') && session.carImages === undefined && !significantChanges;

    if (
      (significantChanges || imagesClearedExternally) &&
      !!minimalDataPresent &&
      // Do NOT swap this for canClaimGalleryForFetch — see its docblock (AVS-3995). Calling
      // fetchImages() here even when 2D is disallowed is load-bearing: its is2DFeatureAllowed()
      // check is what forces activeExperience to '3D'.
      session.carImages?.prString !== session.prString // Verify if the prString has changed
    ) {
      fetchImages();
    }
  });
}

/**
 * Fetches images and returns a Promise that resolves when loading completes.
 * Used for comparison validation where we need to wait for images before proceeding.
 * @returns Promise that resolves to true if images loaded successfully, false otherwise
 */
export async function fetchImagesAndWait(): Promise<boolean> {
  if (!is2DFeatureAllowed()) {
    return false;
  }

  const session = userSession().get();

  if (!session.prString && !session.vinOrMarketingCode) {
    return false;
  }

  userSession().setValues({ carImagesStatus: 'loading' });

  const releaseClaim = claimImageFetch();
  const generation = beginImageFetchGeneration(true);

  try {
    const images = await getImages({
      prString: session.prString,
      brand: session.brand!,
      market: session.market!,
      vinOrMarketingCode: session.vinOrMarketingCode,
    });

    // Same live-state staleness check and decline/failure split as `fetchImages` (AVS-4381).
    // This leg is reached by comparison-mode prefetches, which can be cancelled mid-flight.
    if (!describesCurrentCar(images)) {
      releaseSpinnerClaim(generation);
      return false;
    }

    const outcome = showImages({ images: images.items, index: 0 });
    if (outcome === 'failed') {
      releaseGalleryAfterFailure(generation);
      return false;
    }
    if (outcome === 'declined') {
      // The gallery was left intact on purpose, but this function always claims the spinner
      // (unlike fetchImages it does not check first), so it always owes it back.
      releaseSpinnerClaim(generation);
      return false;
    }
    return true;
  } catch (error) {
    logger.error('Error fetching 2D content:', error);
    // A superseded request owns nothing: the 'loading' on screen backs the newer fetch, whose
    // own handlers will write the final status. Writing 'unset' here would clear that live
    // spinner, and the toast would report an error for a car that is no longer on screen.
    if (generation !== latestImageFetchGeneration) {
      return false;
    }
    userSession().setValues({ carImagesStatus: 'unset' });
    showToast({
      type: 'error',
      title: i18n._(errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].title()),
      message: i18n._(errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].message()),
      notificationId: errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].notificationId,
    });
    return false;
  } finally {
    // The ledger entry must not outlive the request — a stale owner would hand spinner
    // ownership to an unrelated later fetch.
    if (spinnerClaimOwner === generation) spinnerClaimOwner = null;
    releaseClaim();
  }
}

/**
 * AVS-4353: a dealer landing in 2D with a ready car but no session yet cannot fetch images —
 * `initCarImagesAutoUpdate` gates the fetch on `session.id`, so the gallery is marked
 * 'loading' optimistically while the session registers.
 *
 * `startNewSession()` resolves `undefined` whenever registration yields no id — offline mode,
 * a registration already in flight (`syncStarted`), a non-dealer, or a failed server call —
 * and swallows that outcome rather than throwing. A fire-and-forget caller therefore never
 * learns that no fetch will follow, and nothing clears the status: the gallery spins forever
 * having issued zero image requests, which is the reported "2D image lost" symptom.
 *
 * Owning both outcomes here keeps the failure path from being dropped by the caller, and keeps
 * the transition out of the component (business logic belongs in state/, not containers).
 */
let sessionForImagesInFlight: Promise<void> | undefined;

// Not `async`: an async wrapper would allocate a fresh promise per call, so concurrent callers
// could not share one — the coalescing below depends on returning the identical promise.
export function startSessionForImages(): Promise<void> {
  // The Stream/View effect re-fires on [id, carStatus, activeExperience], so a dealer flipping
  // 2D→3D→2D or swapping cars mid-registration can re-enter while the POST is still in flight.
  // `registerSessionOnServer` dedupes the request itself (via `syncStarted`), but the second
  // caller would still run its own claim/release pair and release the FIRST call's spinner
  // early. Coalesce on the shared promise so one bootstrap owns one claim.
  if (sessionForImagesInFlight) {
    return sessionForImagesInFlight;
  }

  sessionForImagesInFlight = runSessionForImages().finally(() => {
    sessionForImagesInFlight = undefined;
  });

  return sessionForImagesInFlight;
}

/**
 * Whether the optimistic 'loading' put up by the bootstrap is still the bootstrap's to hand back.
 *
 * Another flow (`fetchImagesAndWait` in comparison mode, an integrator `showImages()` push, or a
 * different car being loaded) may have taken ownership meanwhile — releasing then would clobber a
 * live gallery, which is the very failure `runSessionForImages` exists to prevent. An outstanding
 * image request also counts as ownership: a 'loading' backed by a live fetch belongs to that
 * fetch, and clearing it would show an empty gallery while the images are still on the wire.
 */
function bootstrapStillOwnsSpinner(): boolean {
  return userSession().get('carImagesStatus') === 'loading' && liveImageFetchClaims === 0;
}

async function runSessionForImages(): Promise<void> {
  // Only claim the gallery when a fetch can actually follow. Under a manual integrator
  // override — or when images for this car are already loaded — no fetch is coming, and a
  // 'loading' write would hide a perfectly good gallery behind a permanent spinner.
  // One snapshot for both reads, so the claim and the prString it is claimed for can never
  // describe different cars.
  const claimedAt = userSession().get();
  const claimedFor = claimedAt.prString;
  const willFetch = canClaimGalleryForFetch(claimedAt);

  // Initiate registration BEFORE claiming. `registerSessionOnServer` captures its POST body
  // synchronously from `userSession().get()` (session.ts:201), so claiming first would persist
  // a transient `carImagesStatus: 'loading'` server-side — and a customer resuming that session
  // would inherit a spinner they can never redeem, since the auto-fetch is dealer-only. This is
  // the ordering the pre-fix code had; only the release below is new.
  const registration = startNewSession().catch((error) => {
    // Log the message rather than the raw error: in the web-component embed this console is
    // the host page's, and network error objects are an easy channel for response bodies.
    logger.error('Error starting session for 2D content:', error instanceof Error ? error.message : String(error));
    return undefined;
  });

  let claimExpiry: ReturnType<typeof setTimeout> | undefined;

  if (willFetch) {
    userSession().setValues({ carImagesStatus: 'loading' });

    claimExpiry = setTimeout(() => {
      claimExpiry = undefined;
      // Same ownership test as the settled path below: an outstanding image request, or any other
      // flow that has since written the status, owns it and must not be clobbered on our timer.
      if (bootstrapStillOwnsSpinner()) {
        logger.warn('Session registration did not settle in time; releasing the 2D gallery claim');
        userSession().setValues({ carImagesStatus: 'unset' });
      }
    }, SESSION_REGISTRATION_CLAIM_TTL_MS);
    // A pending timer keeps the Node event loop alive; ours must never hold a test run open.
    (claimExpiry as unknown as { unref?: () => void }).unref?.();
  }

  const newSession = await registration;

  // Undefined already if the expiry fired; clearing a settled timer would be harmless anyway.
  if (claimExpiry) {
    clearTimeout(claimExpiry);
  }

  if (!willFetch) {
    return;
  }

  // The POST takes seconds, so re-validate ownership before touching the status — see
  // `bootstrapStillOwnsSpinner`.
  if (!bootstrapStillOwnsSpinner()) {
    return;
  }

  const session = userSession().get();

  // AVS-4381: the car can change while the POST is in flight — the DCC branch in
  // Stream/View.tsx writes prString at session root, bypassing updateVehicle. Keying ownership on
  // `prString === claimedFor` meant a mid-flight car change made us skip the release entirely and
  // leave the spinner up forever with zero requests outstanding. A claim made for a car we are no
  // longer showing is stale by definition: release it and let the new car's own fetch re-claim.
  const carChangedUnderUs = session.prString !== claimedFor;

  // No id means no fetch will ever be triggered, so the claim must be released.
  //
  // On success the handoff is subtler: `registerSessionOnServer` echoes
  // `{...sessionValues, ...newSession}` back (session.ts:205) and `sessionValues` came from
  // `userSessionSchema.parse()`, which materializes `carImagesStatus` at its 'unset' default —
  // so registration usually clears the claim itself and `initCarImagesAutoUpdate` re-claims on
  // the id change. Where the claim DOES survive, still re-check the auto-updater's
  // preconditions: an integrator flipping to `imageOverrideMode: 'manual'` mid-POST makes it
  // bail, which would strand the spinner exactly as before this fix.
  const handoffWillFetch = !carChangedUnderUs && !!newSession?.id && canClaimGalleryForFetch(session);

  if (!handoffWillFetch) {
    userSession().setValues({ carImagesStatus: 'unset' });
  }
}

/**
 * Whether a fetched payload describes the car the session is showing RIGHT NOW (AVS-4381).
 *
 * Reads live state rather than the snapshot taken when the request was issued — `showImages`
 * re-reads the session itself, so testing staleness against a frozen snapshot let the two
 * disagree and wrote one car's images under another car's prString.
 *
 * Both identifiers must be compared as *present* values. The previous check compared them raw,
 * so a payload with no `okapiCode` "matched" a session with no `vinOrMarketingCode` —
 * `undefined === undefined` — and every Audi-brand response passed the staleness gate
 * unconditionally, which is what let the wrong-car write through.
 */
function describesCurrentCar(images: { prString?: string; okapiCode?: string }): boolean {
  const current = userSession().get();
  const matchesPrString = !!images.prString && images.prString === current.prString;
  const matchesOkapiCode = !!images.okapiCode && images.okapiCode === current.vinOrMarketingCode;
  return matchesPrString || matchesOkapiCode;
}

/**
 * Hand back the optimistic 'loading' spinner, if this request currently owns it. Never touches
 * a status some other flow has since put up — including a successor fetch's: once a newer
 * request has superseded this one, the 'loading' on screen is the successor's to settle.
 */
function releaseSpinnerClaim(generation: number): void {
  if (spinnerClaimOwner !== generation) {
    return;
  }
  spinnerClaimOwner = null;
  if (userSession().get('carImagesStatus') === 'loading') {
    userSession().setValues({ carImagesStatus: 'unset' });
  }
}

/**
 * Settle the gallery after a payload we could not show (AVS-4383 review).
 *
 * `showImages` answering 'failed' says nothing about who owns the status on screen. Writing
 * 'unset' unconditionally reintroduces the exact defect this file exists to fix, just through the
 * new path: a background refetch never claims the spinner — the gallery is already 'ready' — so a
 * payload it cannot show (no prString, failed validation, or a write that did not land) would wipe
 * a gallery still perfectly valid for the car on screen. Ownership decides here, the same way it
 * already does on the 'declined' and rejection paths:
 *
 *  - superseded by a newer request        → the 'loading' on screen backs the successor, not us;
 *  - we own the optimistic spinner        → hand it back, or the failure strands it;
 *  - a 'ready' gallery for the current car → our failure does not make it wrong. Leave it.
 *
 * Only a status backing nothing valid is cleared.
 *
 * `initCarImagesAutoUpdate` would repair most of that clobber on the next change event, but a
 * repair is not a licence to break the invariant: it costs a visible flicker and a synced write,
 * and it does not cover everything. A blocked car (AVS-4270) is deliberately 'ready' with ZERO
 * items so Stream/View can render "Images are not available" — the self-heal's `items.length > 0`
 * guard skips exactly that case, so there the clobber is permanent. Hence 'ready' for the current
 * car, not item count, is the test.
 */
function releaseGalleryAfterFailure(generation: number): void {
  if (generation !== latestImageFetchGeneration) {
    return;
  }

  if (spinnerClaimOwner === generation) {
    releaseSpinnerClaim(generation);
    return;
  }

  const session = userSession().get();
  if (session.carImagesStatus === 'ready' && session.carImages?.prString === session.prString) {
    return;
  }

  userSession().setValues({ carImagesStatus: 'unset' });
}

export function fetchImages() {
  if (!is2DFeatureAllowed()) {
    return;
  }

  const session = userSession().get();

  if (!session.prString && !session.vinOrMarketingCode) {
    return;
  }

  // don't show the loading state if something is already visible to the user
  const claimedTheSpinner = session.carImagesStatus === 'unset';
  if (claimedTheSpinner) {
    userSession().setValues({ carImagesStatus: 'loading' });
  }

  const releaseClaim = claimImageFetch();
  const generation = beginImageFetchGeneration(claimedTheSpinner);

  getImages({
    prString: session.prString,
    brand: session.brand!,
    market: session.market!,
    vinOrMarketingCode: session.vinOrMarketingCode,
  })
    .then((images) => {
      // Make sure it's not a stale request from another car. Read LIVE state, not the snapshot
      // taken when this request was issued (AVS-4381): `showImages` re-reads the session itself,
      // so testing staleness against a frozen `session` let the two disagree. A switch to another
      // car and back mid-flight then passed this check and wrote THIS car's images under the
      // OTHER car's prString — a wrong-car gallery marked 'ready', with no error anywhere.
      if (!describesCurrentCar(images)) {
        // Not for the car on screen any more. Hand the spinner back if it is ours, otherwise
        // leave whatever is showing alone.
        releaseSpinnerClaim(generation);
        return;
      }

      // A decline is not a failure: the gallery was left untouched on purpose (we are in 3D, or
      // 2D is switched off) and is still valid for the current car. Only a genuine failure
      // releases someone else's gallery — but our OWN optimistic spinner must always be handed
      // back, or the decline strands it at 'loading' forever.
      const outcome = showImages({ images: images.items, index: 0 });
      if (outcome === 'failed') {
        releaseGalleryAfterFailure(generation);
      } else if (outcome === 'declined') {
        releaseSpinnerClaim(generation);
      }
    })
    .catch((error) => {
      logger.error('Error fetching 2D content:', error);
      // A superseded request owns nothing: the 'loading' on screen backs the newer fetch, whose
      // own handlers will write the final status. Writing 'unset' here would clear that live
      // spinner, and the toast would report an error for a car that is no longer on screen.
      if (generation !== latestImageFetchGeneration) {
        return;
      }
      userSession().setValues({ carImagesStatus: 'unset' });
      showToast({
        type: 'error',
        title: i18n._(errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].title()),
        message: i18n._(errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].message()),
        notificationId: errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].notificationId,
      });
    })
    // Released only after the handlers have written their result: until then this fetch still
    // owns the gallery status. Attached last so it adds no microtask ahead of `.then`.
    .finally(() => {
      // The ledger entry must not outlive the request — a stale owner would hand spinner
      // ownership to an unrelated later fetch.
      if (spinnerClaimOwner === generation) spinnerClaimOwner = null;
      releaseClaim();
    });
}

/**
 * Outcome of a `showImages()` call (AVS-4381 / AVS-4383).
 *
 * `showImages` used to answer `true | undefined`, collapsing five semantically different exits
 * into one falsy value. Every caller then read that as "no images" and wrote
 * `carImagesStatus: 'unset'` — including for a *decline*, which is not a failure. Declining while
 * the dealer is in 3D clobbered a gallery that was still perfectly valid for the current car into
 * a state `initCarImagesAutoUpdate` cannot recover (its guard is `carImages.prString !== prString`,
 * so "already loaded" images are never refetched). `carImagesStatus` is on `sharedSessionSchema`,
 * so that state also persisted and survived a reload — which is AVS-4381.
 *
 *  - `shown`    — the gallery was updated and is on screen.
 *  - `declined` — nothing was written, and nothing is wrong. Leave the gallery exactly as it was.
 *  - `failed`   — the payload could not be shown. The caller owns the recovery.
 */
export type TShowImagesResult = 'shown' | 'declined' | 'failed';

export function showImages({
  images = [],
  index = 0,
  override = false,
}: { images?: ICarImageElement[]; index?: number; override?: boolean } = {}): TShowImagesResult {
  if (!is2DFeatureAllowed()) {
    return 'declined';
  }

  const session = userSession().get();

  // Prevent carImages updates when in 3D mode - images are only relevant for 2D experience
  // This prevents race conditions where carImages sync can overwrite aveSessionId during session startup
  // EXCEPTION: Allow images in 3D mode when comparison mode is active (selecting or on),
  // or when an integrator explicitly overrides via the runtime API.
  if (session.activeExperience === '3D' && session.comparisonMode === 'off' && !override) {
    logger.info('Skipping carImages update: in 3D mode with comparison off where images are not used');
    return 'declined';
  }

  // If no images are provided, we fetch them
  // this may be an error from the runtime call or attempt to remove temp images
  if (images === null) {
    fetchImages();
    return 'declined';
  }

  if (!session.prString) {
    logger.error('No prString available to show car images');
    return 'failed';
  }

  // Snapshot the configuration these images were rendered for (AVS-4399).
  //
  // `session` is the live `$data` object, not a copy — `State.get()` with no key returns the
  // instance itself and `setValues` mutates it in place. `updateVehicle` rebases off the vehicle
  // REGISTRY entry, so when a host wrote prString at session root only (DCC), the rebase reverts
  // root prString underneath us mid-call. Comparing the write against a live value therefore asked
  // "does root still say what it said a moment ago", when the only question that matters is "did
  // the gallery I just wrote land, keyed to the car I wrote it for".
  const targetPrString = session.prString;

  const clampedIndex = clampImageIndex(index, images.length);
  if (clampedIndex !== index) {
    logger.warn(`Provided image index ${index} is out of bounds (0-${images.length - 1}). Resetting to 0.`);
  }
  index = clampedIndex;

  if (validateImages(images)) {
    // AVS-4270: a blocked (outdated) car will never get images — mark the gallery
    // 'ready' so Stream/View renders the full-screen "Images are not available"
    // panel, and skip the toast that panel supersedes (PO decision 3)
    const emptyForBlockedCar = !images.length && userSession().is3DBlocked;
    userSession().setValues(
      {
        // Set override mode to 'manual' if integrator provided images
        imageOverrideMode: override ? 'manual' : session.imageOverrideMode,
      },
      { emit: false }
    );
    updateVehicle({
      carImages: {
        prString: targetPrString,
        items: images,
      },
      // when there are no images, show the default background (blocked cars excepted — see above)
      carImagesStatus: images.length || emptyForBlockedCar ? 'ready' : 'unset',
    });

    // Select the specified image index (will sync via registry if Micro2DApp is mounted)
    if (images.length > 0) {
      selectImage(index);
    }

    if (!images.length && !emptyForBlockedCar) {
      showToast({
        type: 'info',
        title: i18n._(errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].title()),
        message: i18n._(errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].message()),
        notificationId: errorCollection[EErrorCode.IMAGES_NOT_AVAILABLE].notificationId,
      });
    }

    // `updateVehicle` silently no-ops when there is no active vehicle entry
    // (vehicleRegistry.ts:429). Reporting 'shown' for a write that never landed would leave the
    // caller believing the gallery is on screen and skip its recovery — so read the write back
    // rather than assuming it.
    if (userSession().get('carImages')?.prString !== targetPrString) {
      logger.error('Car images could not be stored: no active vehicle to attach them to');
      return 'failed';
    }

    return 'shown';
  }

  logger.error('Car images payload failed validation');
  return 'failed';
}

// If we used custom images, we may want to reset and go back to the fetched images
// this will reset the index too. In the future we may want to create a smarter reset procedure
export function resetImages() {
  fetchImages();
}

/**
 * Allows integrators to give image control back to 3D Exhibition.
 * This resets the override mode to 'auto' and fetches images from our APIs.
 * Useful when integrators want to stop providing custom images and let 3DE take over.
 */
export function resetImageOverride() {
  userSession().setValues({ imageOverrideMode: 'auto' });
  fetchImages();
}

// verify if 2D is (still) allowed
function is2DFeatureAllowed() {
  if (!tenantConfig().isAllowed('2D')) {
    if (tenantConfig().isLoaded && !userSession().sessionEnded) {
      // if 2D feature is disabled, we will load 3D instead
      userSession().setValues({ activeExperience: '3D' });
    }

    return false;
  }
  return true;
}

/**
 * Validates the structure of car images.
 * Each image object is expected to have at least a 'source' property of type string.
 * @param images - Array of car image objects to validate.
 * @returns true if all images are valid, false otherwise.
 */
function validateImages(images: ICarImageElement[] = []) {
  for (const img of images) {
    if (!isObject(img) || typeof img.source !== 'string') {
      logger.error('Invalid car image structure:', img);
      return false;
    }
  }
  return true;
}

export function thumbnail(vehicle: IVehicle) {
  const firstImage = vehicle?.carImages?.items?.[0];
  if (firstImage && firstImage.type === 'image') {
    return generateThumbnailUrl(firstImage.source);
  }
  return undefined;
}
